import User from 'users';
import Site from 'site';
import WixSelector from 'wixSelector';
import Location from 'location';
import Window from 'window';
import * as pm from './../privates/postMessage';
import platformUtils from 'platformUtils';
import ga from './../privates/analytics';
import * as ui from 'ui';
import Emitter from './../Emitter';
import Storage from './../Storage';
import formFactor from 'privates/formFactor';
import {onReady, triggerOnReady, onRender, triggerOnRender} from 'privates/readyManager';
import {setLightboxContext} from 'privates/contextService';
import compInitializeService from 'privates/compInitializeService/compInitializeService';
import {initValidationsService} from 'privates/validationsService/validationsService';
import * as urlUtils from 'privates/urlUtils';
import assign from 'lodash/assign';

let storage;
const _wixSelector = new WixSelector();
const emitter = new Emitter();

const location = new Location();
const window = new Window();
const site = new Site();
const user = new User();

const getStorage = (appId, storageValue) => {
    if (!storage) {
        storage = new Storage();
    }
    return storage.createScopedStorage(appId, storageValue);
};
/**
 * @namespace $w
 * @summary The `$w` namespace contains everything you need in order to work
 *  with your site's components. It contains all of the UI elements, nodes, and
 *  events that make up your site. It also includes the [`$w()`](#w),
 *  [`onReady()`](#onReady) function.
 *
 *  The APIs in `$w` can only be used in front-end code.
 *
 *  You do not need to import `$w`.
 */

/**
 * @function $w
 * @memberof $w
 * @syntax
 * function $w(selector: string): Element | Element[]
 * @summary Selects and returns elements from a page.
 * @description
 *  The `$w()` function selects single or multiple elements by ID or type.
 *
 *  To select by ID, pass a selector string with the hash symbol
 *  (`#`) followed by the ID of the item you want to select (e.g. `"#myElement"`).
 *  The function will return the selected element object.
 *
 *  To select by type, pass a selector string with the name of
 *  the type without the preceding `#` (e.g. `"Button"`). The function will return
 *  an array of the selected element objects. An array is returned even if one
 *  or no elements are selected.
 *
 *  To select using multiple selectors, pass a selector string with
 *  multiple selectors separated by commas. The selectors in the
 *  comma-separated string can be ID selectors, type selectors, or a mixture of
 *  the two. The function will return an array of the selected element objects.
 *  An array is returned even if one or no elements are selected. If two or more
 *  selectors select the same element, it will still be returned only once in
 *  the array.
 * @param {external:String} getWixSelector A selector or multiple comma-separated selectors.
 * @returns {$w.Element|$w.Element[]} A single element or an array of elements.
 * @snippet [$w-select_by_name.es6=Select an element using its ID]
 * @snippet [$w-select_by_type.es6=Select elements by type]
 * @snippet [$w-select_images.es6=Select all the images on the page]
 * @snippet [$w-select_combo.es6=Select elements using a multiple selectors]
 * @snippet [$w-select_and_run.es6=Hide all the page's images]
 */

/**
 * @callback $w.ReadyHandler
 * @summary Function that runs when all page elements have finished loading.
 * @returns {Promise}
 * @fulfill {void} When the `onReady` function has completed.
 */


/**
 * @function onReady
 * @syntax
   * function onReady(initFunction: ReadyHandler): void
   * callback ReadyHandler(): Promise&lt;void&gt;
 * @summary Sets the function that runs when all the page elements have finished loading.
 * @description
 *  Use the `onReady()` function for code you want to run before the user starts
 *  interacting with your page.
 *
 *  The following code should be placed inside the `onReady()` event handler:
 *
 *  + Initialization of element properties - Example: setting a text element's initial text value.
 *  + Function calls on elements to set their initial state - Example: disabling a button.
 *  + Dynamic event handlers that you want bound when the page loads - Example: setting an event handler to be called when the mouse pointer enters an element.
 *
 *
 *  Return a Promise from the handler function to force the page to wait for your
 *  Promise to resolve before displaying the page to users.
 *
 *  For example, suppose you are using a query to retrieve data that you want to
 *  display in a table. If you don't return the query promise the table might show
 *  without your data until the query's Promise resolves. Instead return the query
 *  Promise so that the page doesn't load for users until the query returns results
 *  and the table is populated.
 *
 *  Static event handlers, those that are wired in the Properties panel
 *  in the Editor, are not placed inside inside the `onReady()` event handler.
 *
 *  The `onReady()` function in the **Site** tab is called before the
 *  `onReady()` function in the **Page** tab.
 *
 * @note
 *  In certain circumstances the `onReady()` event handler is called twice, once
 *  server-side and once client-side. You can use this behavior to improve your
 *  page's load time. However, you need to be aware that if your `onReady()` code
 *  causes a side effect, such as inserting an item into a collection, that side
 *  effect might happen twice. To learn more about the rendering process, see
 *  [here](https://support.wix.com/en/article/about-the-page-rendering-process).
 * @param {$w.ReadyHandler} initFunction The name of the function or the function expression to run when the page has finished loading.
 * @snippet [$w-onReady-anonymous-long.es6=Run code in an anonymous function when the page loads]
 * @snippet [$w-onReady-promise.es6=Wait for a Promise to resolve]
 *  This example demonstrates the technique of returning a Promise so that the
 *  page doesn't load until the Promise resolves. Here, we wait for the query to
 *  finish and populate a table before displaying the page to users.
 * @snippet [$w-onReady-full.es6=Where to place code]
 *  This example demonstrates which code should be placed inside the `onReady()`
 *  event handler.
 * @see [$w( )](#w)
 * @memberof $w
 */

const getSelector = (controllerId, logName) => {
  const $w = _wixSelector.scoped$W(controllerId, logName);
  assign($w, emitter.createScopedEmitter(controllerId));


  $w.onReady = function (callback) {
      onReady(callback, logName);
  };
  $w.onRender = function (callback) {
      onRender(callback, logName);
  };
  return $w;
};

const getScopedGlobalApis = (appDefId) => {
   return {
       user: new User(appDefId),
       location: new Location(appDefId),
       window: new Window(appDefId),
       site: new Site(appDefId)
   };
};

const __INTERNAL__ = (function() {

    function validateGlobalsParameters (platformUtils) {
        if (!platformUtils) {
            throw new Error(`invalid parameter value for platformUtils: ${platformUtils}`);
        }

        if (!platformUtils.RGI) {
            throw new Error(`invalid parameter value for remoteGlobalsInterface: ${platformUtils.RGI}`);
        }
    }

    function validateParameters (platformUtils, siteInfo, contextId) {
        if (!platformUtils) {
            throw new Error(`invalid parameter value for platformUtils: ${platformUtils}`);
        }

        if (!platformUtils.RMI) {
            throw new Error(`invalid parameter value for remoteModelInterface: ${platformUtils.RMI}`);
        }

        if (!platformUtils.linkUtils) {
            throw new Error(`invalid parameter value for linkUtils: ${platformUtils.linkUtils}`);
        }

        if (!platformUtils.typeUtils) {
            throw new Error(`invalid parameter value for typeUtils: ${platformUtils.typeUtils}`);
        }

        if (!siteInfo) {
            throw new Error(`invalid parameter value for siteInfo: ${siteInfo}`);
        }

        if (!contextId) {
            throw new Error(`invalid parameter value for contextId: ${contextId}`);
        }
    }

    function dumpModel () {
        return {
            RMI: platformUtils.getRMI('dumpModel').toJson(),
            RGI: platformUtils.getRGI().toJson()
        };
    }

    function getDefaultUpdateHandler(contextId) {
        return function delegateCommandToViewer(compId, type, changes, callback) {
            switch(type) {
                case 'executeBatch':
                    pm.sendMessage(pm.Intent.WIX_CODE, 'executeBatch', compId, contextId, changes, callback);
                    break;
                case 'registerEvent':
                    pm.sendMessage(pm.Intent.WIX_CODE, 'registerEvent', compId, contextId, changes, callback);
                    break;
                case 'unregisterAll':
                    pm.sendMessage(pm.Intent.WIX_CODE, 'unregisterAll', compId, contextId, changes, callback);
                    break;
                case 'behavior':
                    pm.sendMessage(pm.Intent.WIX_CODE, 'executeBehavior', compId, contextId, changes, callback);
                    break;
                default:
                    pm.sendMessage(pm.Intent.WIX_CODE, `${type}Changed`, compId, contextId, changes, callback);
                    break;
            }
        };
    }

    function initGlobals(platformUtilities) {
        validateGlobalsParameters(platformUtilities);
        platformUtils.initGlobals(platformUtilities);
    }

    function initModel (...args) {
        validateParameters(...args);
        const [platformUtilities, siteInfo, contextId, onUpdateHandler] = args;
        const RMI = platformUtilities.RMI;
        const componentsHooks = platformUtilities.componentsHooks;
        const HOOKS = RMI.getHooks();
        RMI.registerHook(HOOKS.data, 'wysiwyg.viewer.components.Repeater', componentsHooks.repeaters.updateRepeaterItems);
        RMI.registerHook(HOOKS.all, null, componentsHooks.repeaters.syncRepeaterItemData);
        formFactor.init(siteInfo.deviceType);
        _wixSelector.initialize(RMI, contextId, emitter);
        platformUtils.init(platformUtilities, _wixSelector, onUpdateHandler || getDefaultUpdateHandler(contextId));

        initValidationsService();
        compInitializeService.componentsInitialize(RMI, contextId, RMI.getComponents());
    }

    function setStaticEventHandlers (eventHandlers, contextId) {
        platformUtils.setStaticEventHandlers(eventHandlers, contextId);
    }

    function getEventHandler(name) {
       return platformUtils.getStaticEventHandler(name, true);
    }

    function onMessage(event) {
        pm.onMessage(event);
    }

    function initEnv(queryParameters) {
        window.init(queryParameters);
    }

    function addEnvParams(params) {
        urlUtils.addQueryParameters(params);
    }

    function setContextId(contextId) {
        platformUtils.setContextId(contextId);
    }

    function setWarmupData(controllerId, data) {
        pm.sendMessage(pm.Intent.WIX_CODE_WARMUP, pm.MessageTypes.SET_WARMUP_DATA, {controllerId, data});
    }

    function getWarmupData(controllerId) {
        const uw = urlUtils.getQueryParameter("userWarmup");
        return uw && uw[controllerId];
    }

    return {
        dumpModel,
        initGlobals,
        initModel,
        setStaticEventHandlers,
        getEventHandler,
        onMessage,
        addEnvParams,
        initEnv,
        setWarmupData,
        getWarmupData,
        setContextId,
        triggerOnReady,
        triggerOnRender,
        setLightboxContext
    };
})();


self['wix-location'] = location;
self['wix-window'] = window;
self['wix-site'] = site;
self['wix-users'] = user;

export {
    site,
    location,
    window,
    user,
    __INTERNAL__,
    ui,
    getSelector,
    getStorage,
    emitter,
    onReady,
    ga,
    getScopedGlobalApis
};



// WEBPACK FOOTER //
// ./js/modules/targets/wixCode.es6